import React, { memo, useEffect, useState } from 'react';
import { Box, Button, LinearProgress, Paper, Stack, Typography, alpha } from '@mui/material';
import { AutoAwesomeRounded } from '@mui/icons-material';
import type { Run } from '../../../services/timetableService';

interface RunBannerProps {
  run: Run;
  canStop: boolean;
  onStop: () => void;
  /** What is being planned, e.g. "Class 8 C" (absent for the whole school). */
  scopeLabel?: string;
  onReview: () => void;
}

/** A generation in progress: what it is doing, how far along, and a way to stop with the best so far. */
const RunBanner = memo<RunBannerProps>(({ run, canStop, onStop, scopeLabel, onReview }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.max(0, Math.round((now - new Date(run.createdAt).getTime()) / 1000));
  const { stage, placed, total } = run.progress;
  const placing = stage === 'placing';
  const pct = total ? Math.round((placed / total) * 100) : 0;
  const timePct = Math.min(100, (elapsed / run.timeLimitSeconds) * 100);

  if (run.status === 'review') {
    const n = run.moves?.length ?? 0;
    return (
      <Paper
        variant="outlined"
        sx={(theme) => ({ mb: 2, px: 2.5, py: 1.75, borderRadius: '12px', borderColor: alpha(theme.palette.warning.main, 0.5), bgcolor: alpha(theme.palette.warning.main, 0.06) })}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <AutoAwesomeRounded color="warning" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={650}>{scopeLabel ?? 'The timetable'} is ready, with {n} change{n === 1 ? '' : 's'} to other classes</Typography>
            <Typography variant="caption" color="text.secondary">Nothing has changed yet. Review the moves, then apply or discard them.</Typography>
          </Box>
          {canStop && <Button variant="contained" size="small" onClick={onReview} sx={{ textTransform: 'none', flexShrink: 0 }}>Review changes</Button>}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({ mb: 2, px: 2.5, py: 1.75, borderRadius: '12px', borderColor: alpha(theme.palette.primary.main, 0.35), bgcolor: alpha(theme.palette.primary.main, 0.04) })}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <AutoAwesomeRounded color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={650}>
            {run.stopRequested ? 'Stopping and keeping the best timetable so far…'
              : scopeLabel ? (placing ? `Planning ${scopeLabel}…` : `Improving ${scopeLabel}…`)
              : placing ? `Placing periods: ${placed} of ${total}`
              : `All ${placed} periods placed. Improving the timetable…`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {placing ? 'Every class, teacher and room is planned together.' : 'Removing gaps, spreading subjects across the week and honouring preferences.'}
            {' '}{elapsed}s
          </Typography>
          <LinearProgress variant="determinate" value={placing ? pct : timePct} sx={{ mt: 1, height: 4, borderRadius: 2 }} />
        </Box>
        {canStop && !run.stopRequested && (
          <Button variant="outlined" size="small" onClick={onStop} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Stop and keep best
          </Button>
        )}
      </Stack>
    </Paper>
  );
});

RunBanner.displayName = 'RunBanner';

export default RunBanner;
